import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SeoPageLayout from "@/components/seo/SeoPageLayout";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import CtaBlock from "@/components/seo/CtaBlock";
import useSeo from "@/hooks/useSeo";
import { CASE_STUDIES } from "@/data/seo/caseStudies";
import { breadcrumbSchema, canonicalFor } from "@/lib/seoHelpers";

export default function CaseStudiesIndex() {
  const crumbs = [
    { label: "Home", href: "/" },
    { label: "Case Studies", href: "/work" },
  ];

  useSeo({
    title: "Wedding Invitation Case Studies | Real Client Work | InvitationWala",
    description:
      "Real wedding invitation case studies — celebrity weddings, creator couples, destination stationery and keepsakes designed by InvitationWala.",
    canonical: canonicalFor("/work"),
  });

  const schemas = [breadcrumbSchema(crumbs)];

  return (
    <SeoPageLayout schemas={schemas}>
      <div
        data-testid="case-studies-index"
        className="max-w-[1200px] mx-auto px-6 md:px-12 py-6 md:py-12"
      >
        <Breadcrumbs items={crumbs} />

        <div className="mb-12 md:mb-16 max-w-3xl">
          <span className="overline text-[var(--iw-gold)]">Selected Works · The Full Stories</span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-display text-[var(--iw-ivory)] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-4 tracking-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Every invite has a story.
            <br />
            <span className="italic gold-rose-gradient">These are ours.</span>
          </motion.h1>
          <p className="text-[var(--iw-muted)] text-base md:text-lg leading-relaxed mt-6">
            From a Team India cricketer's wedding film to hand-painted destination
            stationery — real briefs, real couples, and the craft behind each
            invitation we delivered.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 md:gap-8">
          {CASE_STUDIES.map((cs, idx) => (
            <motion.div
              key={cs.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (idx % 2) * 0.08, ease: [0.16, 1, 0.3, 1] }}
            >
              <Link
                to={`/work/${cs.slug}`}
                className="group block border border-white/10 hover:border-[var(--iw-gold)]/50 transition-colors overflow-hidden"
                data-testid={`case-card-${cs.slug}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={cs.cover}
                    alt={`${cs.title} — InvitationWala case study`}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                  />
                  <div
                    aria-hidden="true"
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(10,10,10,0) 50%, rgba(10,10,10,0.85) 100%)",
                    }}
                  />
                </div>
                <div className="p-5 md:p-6">
                  <span className="overline text-[var(--iw-gold)]">{cs.category}</span>
                  <h2
                    className="font-serif-display text-[var(--iw-ivory)] text-xl md:text-2xl leading-tight mt-2 group-hover:text-[var(--iw-gold)] transition-colors"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    {cs.client}
                  </h2>
                  <p className="text-[var(--iw-muted)] text-sm leading-relaxed mt-2 line-clamp-2">
                    {cs.intro}
                  </p>
                  <span className="inline-flex items-center gap-1.5 overline text-[var(--iw-gold)] mt-4">
                    Read the story <ArrowUpRight size={12} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <CtaBlock
          title="Your story could be next."
          subtitle="Share your celebration's details on WhatsApp — concept and quote within the hour."
          prefilledMessage="Hi InvitationWala, I saw your case studies and I'd like a custom invitation for my celebration."
          testid="cta-case-index"
        />
      </div>
    </SeoPageLayout>
  );
}
